"use client";

import { useCallback, useEffect, useRef } from "react";
import useSWR from "swr";
import { useFetchSurveyService } from "~/services/api/services/questionnaire";
import HTTP_CODES_ENUM from "~/services/api/types/http-codes";
import { useLogEvent } from "~/hooks/useLogEvent";
import type { SurveyData } from "~/types/Survey";

const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
const ERROR_RETRY_COUNT = 2;

type UseSurveyDataReturn = {
    surveyData: SurveyData | null;
    isLoading: boolean;
};

export function useSurveyData(
    category = "",
    fallbackData?: SurveyData | null
): UseSurveyDataReturn {
    const fetchSurvey = useFetchSurveyService();
    const logEvent = useLogEvent();
    const loggedRef = useRef(false);

    const fetcher = useCallback(async (): Promise<SurveyData | null> => {
        const { data, status } = await fetchSurvey(category);

        if (status !== HTTP_CODES_ENUM.OK || !data?.schema) {
            throw new Error(`Survey fetch failed with status ${status}`);
        }

        return data as SurveyData;
    }, [fetchSurvey, category]);

    const { data, error, isLoading } = useSWR<SurveyData | null>(
        category ? `survey-${category}` : null,
        fetcher,
        {
            fallbackData: fallbackData ?? undefined,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateIfStale: false,
            dedupingInterval: FOUR_HOURS_MS,
            errorRetryCount: ERROR_RETRY_COUNT,
            errorRetryInterval: 2000,
            shouldRetryOnError: (err) => {
                return !(err instanceof Error && err.message.includes(HTTP_CODES_ENUM.NOT_FOUND.toString()));                
            },
        }
    );

    useEffect(() => {
        if (isLoading || data || !error || loggedRef.current) return;
        loggedRef.current = true;

        logEvent({
            message: "[useSurveyData] Survey data unavailable, page rendered empty",            
            data: {
                userAgent: navigator.userAgent,
                error: error instanceof Error ? error.message : String(error),
                survey_category: category,                                
            },
            tag: "survey-data",
        });
    }, [isLoading, data, error, category, logEvent]);

    return {
        surveyData: data ?? null,
        isLoading,
    };
}
